<script lang="ts">
  import AnimatedBox from "../components/atoms/AnimatedBox.svelte";
  import AnimationContainer from "../components/atoms/AnimationContainer.svelte";
  import BackButton from "../components/atoms/BackButton.svelte";
  import Breadcrumb from "../components/atoms/Breadcrumb.svelte";
  import BreadcrumbsContainer from "../components/atoms/BreadcrumbsContainer.svelte";
  import EstimationScreen from "../screens/purchaseflow/EstimationScreen.svelte";
  import InformationScreen from "../screens/purchaseflow/InformationScreen.svelte";
  import PriceScreen from "../screens/purchaseflow/PriceScreen.svelte";
  import SummaryScreen from "../screens/purchaseflow/SummaryScreen.svelte";
  import { type PurchaseflowContext, purchaseflowStore } from "../store/purchaseflow";
  import ConfirmationScreen from "../screens/sharedScreens/ConfirmationScreen.svelte";
  import { useUrls } from "../helpers/hooks";
  import { getUrl } from "../store/purchaseflow";
  import { useMachine } from "@xstate/svelte";
  import PurchaseFlowProvider from "../providers/PurchaseFlowProvider.svelte";

  interface Props {
    goToStartScreen: (value: boolean | undefined) => void;
    seller: string;
    confirmationContent: string;
    selectedProductId: number | undefined;
    campaign: string;
    location: string;
    usingMovingParam: boolean;
    forceShowEstimation: boolean;
  }

  let {
    goToStartScreen,
    seller,
    confirmationContent,
    selectedProductId,
    campaign,
    location,
    usingMovingParam,
    forceShowEstimation,
  }: Props = $props();

  const machine = useMachine(purchaseflowStore);
  const { state: store, send } = machine;

  const onBack = () => {
    if ($store.value === "estimation") {
      goToStartScreen(undefined);
    } else {
      send("BACK");
    }
  };

  let isInitialScreenUnmounted = $state(false);

  window.addEventListener("initialScreenUnmounted", () => {
    isInitialScreenUnmounted = true;
  });

  if (usingMovingParam || forceShowEstimation) {
    isInitialScreenUnmounted = true;
  }

  useUrls<PurchaseflowContext>({ onBack, state: store, getUrl, location });

  if (campaign && campaign != "Nyleverans") send("SET_CAMPAIGN", { campaign });

  if (selectedProductId) {
    send("SET_PRODUCT_ID", { selectedProductId });
  }

  let stateKeys = $derived(Object.keys(purchaseflowStore.states));
  let currentPage = $derived(stateKeys.indexOf($store.value.toString()) + 1);

  let isScreenActive = $derived(
    (currentScreen: string): boolean => stateKeys.indexOf(currentScreen) <= currentPage - 1
  );
</script>

<PurchaseFlowProvider {machine}>
  <div class="flex-1 flex align-center flex-col sm:px-0 max-w-content">
    <div
      class="w-full flex items-center flex-row-reverse sm:flex-col justify-between mb-3 md:mb-12 flex-shrink-0"
    >
      <BreadcrumbsContainer>
        <Breadcrumb number={1} active={isScreenActive("price")} class="flex-1">Avtal</Breadcrumb>
        <Breadcrumb number={2} active={isScreenActive("information")} class="flex-1">
          Uppgifter
        </Breadcrumb>
        <Breadcrumb number={3} active={isScreenActive("summary")} class="flex-1"
          >Summering</Breadcrumb
        >
      </BreadcrumbsContainer>
      <div class="ml-2 sm:ml-0 sm:self-start mt-2">
        <BackButton disabled={!isInitialScreenUnmounted} onClick={onBack} />
      </div>
    </div>
    <AnimationContainer class="sm:p-0 pb-6 sm:pb-0">
      {#if $store.value == "estimation"}
        <AnimatedBox>
          <EstimationScreen />
        </AnimatedBox>
      {:else if $store.value == "price"}
        <AnimatedBox>
          <PriceScreen />
        </AnimatedBox>
      {:else if $store.value == "information"}
        <AnimatedBox>
          <InformationScreen />
        </AnimatedBox>
      {:else if $store.value == "summary"}
        <AnimatedBox>
          <SummaryScreen {seller} />
        </AnimatedBox>
      {:else if $store.value == "confirmation"}
        <AnimatedBox>
          <ConfirmationScreen
            confirmationType="purchaseFlow"
            {seller}
            {confirmationContent}
            signId={$store.context.signId}
          />
        </AnimatedBox>
      {/if}
    </AnimationContainer>
  </div>
</PurchaseFlowProvider>
